import { useState } from 'react';
import type { OrgTreeNode, OrgEchelon } from '../api/keycloak-admin/org-config';
import { FONTS } from '../styles/tokens';

interface OrgTreeViewProps {
  nodes: OrgTreeNode[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  onAddChild: (parentId: string) => void;
  onDelete: (id: string) => void;
}

/** Recursive collapsible tree view for org units */
export function OrgTreeView({
  nodes, selectedId, onSelect, onAddChild, onDelete,
}: OrgTreeViewProps): React.JSX.Element {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
      {nodes.map((node) => (
        <TreeNode key={node.id} node={node} depth={0}
          selectedId={selectedId} onSelect={onSelect}
          onAddChild={onAddChild} onDelete={onDelete} />
      ))}
    </div>
  );
}

/** Single node in the org tree */
function TreeNode({
  node, depth, selectedId, onSelect, onAddChild, onDelete,
}: {
  node: OrgTreeNode; depth: number; selectedId: string | null;
  onSelect: (id: string) => void; onAddChild: (parentId: string) => void;
  onDelete: (id: string) => void;
}): React.JSX.Element {
  const [expanded, setExpanded] = useState(true);
  const [hovered, setHovered] = useState(false);
  const hasChildren = node.children.length > 0;
  const isSelected = node.id === selectedId;

  return (
    <div>
      <div
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onClick={() => onSelect(node.id)}
        style={{
          display: 'flex', alignItems: 'center', gap: '6px',
          padding: '6px 8px', paddingLeft: `${depth * 20 + 8}px`,
          borderRadius: '6px', cursor: 'pointer',
          backgroundColor: isSelected ? 'rgba(1, 169, 237, 0.1)' : (hovered ? 'rgba(255,255,255,0.04)' : 'transparent'),
          transition: 'background-color 0.15s',
        }}
      >
        <ExpandToggle hasChildren={hasChildren} expanded={expanded}
          onToggle={() => setExpanded(!expanded)} />
        <EchelonBadge echelon={node.echelon} />
        <span style={{
          flex: 1, fontFamily: FONTS.body, fontSize: '13px',
          color: isSelected ? '#01A9ED' : 'inherit',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {node.name}
        </span>
        <span style={{ fontFamily: FONTS.body, fontSize: '10px', color: '#888' }}>
          {node.shortName}
        </span>
        <div style={{ display: 'flex', gap: '2px', opacity: hovered ? 1 : 0, transition: 'opacity 0.15s' }}>
          <SmallBtn label="+" title="Add child" onClick={() => onAddChild(node.id)} />
          <SmallBtn label="\u2715" title="Delete" onClick={() => onDelete(node.id)} />
        </div>
      </div>
      {expanded && hasChildren && (
        <div>
          {node.children.map((child) => (
            <TreeNode key={child.id} node={child} depth={depth + 1}
              selectedId={selectedId} onSelect={onSelect}
              onAddChild={onAddChild} onDelete={onDelete} />
          ))}
        </div>
      )}
    </div>
  );
}

/** Expand/collapse chevron */
function ExpandToggle({ hasChildren, expanded, onToggle }: {
  hasChildren: boolean; expanded: boolean; onToggle: () => void;
}): React.JSX.Element {
  if (!hasChildren) {
    return <span style={{ width: '14px', display: 'inline-block' }} />;
  }
  return (
    <button onClick={(e) => { e.stopPropagation(); onToggle(); }}
      style={{ padding: '2px', background: 'none', border: 'none', cursor: 'pointer', fontSize: '8px', color: '#888', width: '14px' }}>
      {expanded ? '\u25BC' : '\u25B6'}
    </button>
  );
}

/** Colored echelon badge */
function EchelonBadge({ echelon }: { echelon: OrgEchelon }): React.JSX.Element {
  const ECHELON_COLORS = new Map<OrgEchelon, { bg: string; text: string }>([
    ['strategic', { bg: 'rgba(168, 85, 247, 0.15)', text: '#A855F7' }],
    ['operational', { bg: 'rgba(59, 130, 246, 0.15)', text: '#3B82F6' }],
    ['tactical', { bg: 'rgba(34, 197, 94, 0.15)', text: '#22C55E' }],
  ]);
  const color = ECHELON_COLORS.get(echelon) ?? { bg: 'rgba(160,160,160,0.15)', text: '#999' };

  return (
    <span style={{
      padding: '2px 6px', borderRadius: '4px',
      backgroundColor: color.bg, color: color.text,
      fontFamily: FONTS.heading, fontSize: '9px',
      fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em',
    }}>
      {echelon.slice(0, 3)}
    </span>
  );
}

/** Small action button */
function SmallBtn({ label, title, onClick }: {
  label: string; title: string; onClick: () => void;
}): React.JSX.Element {
  return (
    <button
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      title={title}
      style={{
        padding: '2px 4px', borderRadius: '4px', border: 'none',
        background: 'transparent', cursor: 'pointer',
        fontSize: '11px', color: '#888', lineHeight: 1,
      }}
    >
      {label}
    </button>
  );
}
